import type {
  InitOptions as I18nInitOptions,
  Namespace,
  ResourceKey,
  TOptionsBase,
} from 'i18next';

import i18next from 'i18next';

import {pollAsync} from '@yourcoach/async-utils';

import {DEFAULT_LOCALE} from './constants';
import {init} from './init';

const isHermesRuntime =
  typeof globalThis === 'object' && 'HermesInternal' in globalThis;

class I18n {
  _instance = i18next;
  defaultLocale = DEFAULT_LOCALE;
  isInitialized = false;
  language = i18next.language;

  async init(options: I18n.InitOptions) {
    if (this.isInitialized) {
      return;
    }

    const i18nOptions: I18nInitOptions = {
      debug: process.env.NODE_ENV === 'development' && !isHermesRuntime,
      fallbackLng: DEFAULT_LOCALE,
      fallbackNS: options.fallbackNS,
      interpolation: {
        escapeValue: false,
      },
      load: 'currentOnly',
      ns: options.namespaces || [],
      parseMissingKeyHandler: key => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(`[i18n] Missing i18n key: ${i18next.language}|${key}`);
        }

        return `[${i18next.language}] NT:${key}`;
      },
      resources: options.resources,
    };

    await init(i18nOptions);

    await this.loadNamespaces([
      ...(options.namespaces || []),
      'file-size',
      'date-time',
      'geo',
    ]);

    this.isInitialized = true;

    this.language = i18next.language;

    this.listenToLanguageChange();
  }

  async awaitForNamespaces(namespaces: I18n.Namespace[]) {
    return pollAsync(() => i18next.hasLoadedNamespace(namespaces), {
      timeout: 30 * 1000,
      timeoutMessage: `Could not load namespaces: ${namespaces.join(', ')}`,
    });
  }

  /**
   * Formats a number of bytes into a human-readable string with appropriate unit (B, KB, MB, GB, TB)
   * The value is rounded to 2 decimal places and trailing zeros are removed
   *
   * @param size - Number of bytes to format
   * @returns Formatted string with size and unit (e.g. "1.21 MB")
   *
   * @example
   * fileSize(1234) // "1.21 KB"
   * fileSize(1234 * 1024) // "1.21 MB"
   * fileSize(1024 * 1024) // "1 MB"
   */
  fileSize(size: number) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

    let index = 0;
    let value = size;

    while (value >= I18n.BYTES_IN_KILOBYTE && index < units.length - 1) {
      value /= I18n.BYTES_IN_KILOBYTE;
      index++;
    }

    // Round to 2 decimal places and remove trailing zeros
    const roundedValue = Number(value.toFixed(2));

    /**
     * @i18n_parser
     * t('file-size:B')
     * t('file-size:KB')
     * t('file-size:MB')
     * t('file-size:GB')
     * t('file-size:TB')
     */
    return this.t(`file-size:${units[index]}`, {
      size: roundedValue,
    });
  }

  isNamespacesLoaded(namespaces: I18n.Namespace[]) {
    return i18next.hasLoadedNamespace(namespaces);
  }

  async loadNamespaces(namespaces: I18n.Namespace[]) {
    await pollAsync(() => this._instance.isInitialized, {
      timeout: 30 * 1000,
      timeoutMessage: 'i18n is not initialized!',
    });

    if (i18next.hasLoadedNamespace(namespaces)) {
      return Promise.resolve();
    }

    return i18next.loadNamespaces(namespaces);
  }

  t<K extends string = string & {}>(
    key: I18n.NamespacedKey<K>,
    options?: I18n.TFunctionOptions,
  ) {
    const result = i18next.t(key.trim(), options);

    this.checkTResult(result, key);

    return result;
  }

  tArr<K extends string = string & {}>(
    key: I18n.NamespacedKey<K>,
    options?: I18n.TArrFunctionOptions,
  ): string[] {
    const result = i18next.t(key.trim(), {
      ...options,
      joinArrays: options?.join,
      returnObjects: true,
    });

    this.checkTResult(result, key);

    if (Array.isArray(result)) {
      return result.map(item => String(item));
    }

    return [String(result)];
  }

  tObj<K extends string = string & {}>(
    key: I18n.NamespacedKey<K>,
    options?: I18n.TObjFunctionOptions,
  ): object {
    const result = i18next.t(key.trim(), {
      ...options,
      returnObjects: true,
    });

    this.checkTResult(result, key);

    if (Array.isArray(result)) {
      return {
        not_object: result,
      };
    }

    return result;
  }

  private checkTResult(result: any, key: string, ns?: Namespace) {
    const namespace = ns || i18next.options.defaultNS || 'NO_NAMESPACE';

    if (!result) {
      if (this.isInitialized) {
        this.log(`Empty key: "${namespace}:${key}"`);
      } else {
        this.log(
          `Attempt to use key: "${namespace}:${key}" before initialization`,
        );
      }
    }
  }

  private listenToLanguageChange() {
    i18next.on('languageChanged', () => {
      this.language = i18next.language;
    });
  }

  private log(message: string) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] ${message}`);
    }
  }

  private static readonly BYTES_IN_KILOBYTE = 1024;
}

export namespace I18n {
  export type InitOptions = {
    fallbackNS?: Namespace;
    namespaces: Namespace[];
    /**
     * use it to load resources during init
     */
    resources?: Resource;
  };

  export type Language = string;

  export type Namespace =
    | 'admin-dashboard'
    | 'b2b-widget'
    | 'b2b-widget-booking'
    | 'b2b-widget-booking-confirmation'
    | 'b2b-widget-coaching-session'
    | 'date-time'
    | 'dtc-page'
    | 'employer-dashboard'
    | 'file-size'
    | 'geo'
    | 'mobile-app'
    | 'notifications'
    | 'onboarding'
    | 'partner-admin-dashboard'
    | 'shared'
    | 'web-app';

  export type NamespacedKey<T extends string = string & {}> =
    T extends `${infer N}:${string}`
      ? N extends Namespace
        ? T
        : '[i18n] INVALID NAMESPACE'
      : '[i18n] NAMESPACE IS REQUIRED';

  export type Resource = Record<Language, ResourceLanguage>;

  export type TArrFunctionOptions = TFunctionBaseOptions & {
    join?: string;
  } & TFunctionInterpolationParams;

  export type TFunctionOptions = TFunctionBaseOptions &
    TFunctionInterpolationParams;

  export type TObjFunctionOptions = TFunctionBaseOptions &
    TFunctionInterpolationParams;

  type ResourceLanguage = Partial<Record<Namespace, ResourceKey>>;

  type TFunctionBaseOptions = Pick<
    TOptionsBase,
    'context' | 'count' | 'defaultValue' | 'lng' | 'ordinal'
  >;

  type TFunctionInterpolationParams = Record<string, unknown>;
}

export const i18n = new I18n();
